<template>
    <div>

        <v-form ref="chartForm" :lazy-validation="true" v-model="formValid">
        <v-row dense>
            <v-col cols="12" sm="6">
                <v-select light v-model="chartType" ref="type" :items="chartTypes" label="Chart Type" :color="'#c42111'" :rules="[() => !!chartType || 'This field is required']" />
            </v-col>

            <v-col cols="12" sm="6">
                <v-text-field light v-model="chartTitle" label="Chart Title" color="#c42111" />
            </v-col>

            <v-col cols="12">
                <v-text-field light v-model="chartSubtitle" label="Chart Subtitle" color="#c42111" />
            </v-col>

            <v-col cols="12" sm="6">
                <v-text-field light v-model="xAxisTitle" label="X-Axis Label" color="#c42111" />
            </v-col>

            <v-col cols="12" sm="6">
                <v-text-field light v-model="yAxisTitle" label="Y-Axis Label" color="#c42111" :disabled="multipleAxis" />
            </v-col>

            <!-- NETWORK ELEMENTS -->
            <v-col cols="12" v-if="$props.transpose">
                <v-autocomplete light eager autocomplete="off" :items="networkElementsList" v-model="selectedNetworkElements" multiple label="Network Elements" color="#c42111" :rules="[() => !!selectedNetworkElements.length || 'This field is required']">
                    <template v-slot:prepend-item>
                        <v-list-item ripple @click="toggleSelectAllNetworkElements">
                            <v-list-item-action>
                                <v-icon :color="selectedNetworkElements.length > 0 ? '#168A14' : ''">{{ networkElementIcon }}</v-icon>
                            </v-list-item-action>
                            <v-list-item-content>
                                <v-list-item-title>Select All Network Elements</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-2"></v-divider>
                    </template>

                    <template v-slot:selection="{ item, index }">
                        <span v-if="index === 0">{{ item }}</span>
                        <span v-if="index === 1" class="grey--text caption"> (+{{ selectedNetworkElements.length - 1 }} others)</span>
                    </template>

                    <template v-slot:append-item>
                        <v-divider class="mb-2"></v-divider>
                        <v-list-item disabled>
                            <v-list-item-avatar color="grey lighten-3">
                            <v-icon color="grey darken">mdi-table-row</v-icon>
                            </v-list-item-avatar>

                            <v-list-item-content v-if="allNetworkElementsSelected">
                                <v-list-item-title>All network elements selected!</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-content v-else-if="someNetworkElementsSelected">
                                <v-list-item-title>Selected Column Count</v-list-item-title>
                                <v-list-item-subtitle>{{ selectedNetworkElements.length }}</v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-content v-else>
                                <v-list-item-title>
                                    Select network elements above
                                </v-list-item-title>
                                <v-list-item-subtitle>
                                    click on a network element to select for charting
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </template>
                </v-autocomplete>
            </v-col>

            <!-- LEGEND CONFIG -->
            <v-col cols="12" sm="6" v-if="$props.transpose">
                <v-autocomplete light autocomplete="off" :items="legendValues" v-model="selectedLegendValues" multiple label="Network Element Legend Label" color="#c42111" :rules="[() => !!selectedLegendValues.length || 'This field is required']" />
            </v-col>
            <v-col cols="12" sm="6" v-if="$props.transpose">
                <v-switch light class="mr-8" style="display: inline-block;" v-model="hideLegend" label="Hide Legend" color="#c42111" />
                <v-switch light style="display: inline-block;" v-model="hideMetricsLegend" label="Hide selected metrics in legend" color="#c42111" :disabled="hideMetricsLegendDisabled" />

                <v-tooltip bottom v-if="hideMetricsLegend && selectedMetrics.length > 1">
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on" color="#FB8C00" large class="ml-2 mb-2">mdi-alert</v-icon>
                    </template>
                    <span>Warning: hiding more than 1 metric can create ambiguity in the chart legend</span>
                </v-tooltip>
            </v-col>

            <!-- METRICS -->
            <v-col cols="12" sm="6" v-if="$props.transpose">
                <v-autocomplete light autocomplete="off" :items="metricsList" v-model="selectedMetrics" multiple label="Metrics" color="#c42111" :rules="[() => !!selectedMetrics.length || 'This field is required']" />
            </v-col>

            <!-- ROWS (only used for non-transposed datasets) -->
            <v-col cols="12" sm="6" v-if="!$props.transpose">
                <v-autocomplete light autocomplete="off" :items="rowsList" item-text="row_name" return-object v-model="selectedRows" multiple label="Rows" color="#c42111" :rules="[() => !!selectedRows.length || 'This field is required']" />
            </v-col>
            
            <!-- COLUMNS -->
            <v-col cols="12" sm="6">
                <v-select light v-model="selectedColumns" ref="cols" :items="columnList" :item-text="'name'" label="Columns" multiple return-object color="#c42111" :rules="[() => !!selectedColumns.length || 'This field is required']">
                    <template v-slot:prepend-item>
                        <v-list-item ripple @click="toggleSelectAllColumns">
                            <v-list-item-action>
                                <v-icon :color="selectedColumns.length > 0 ? '#168A14' : ''">{{ columnIcon }}</v-icon>
                            </v-list-item-action>
                            <v-list-item-content>
                                <v-list-item-title>Select All Columns</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-2"></v-divider>
                    </template>
                    <template v-slot:selection="{ item, index }">
                        <span v-if="index === 0">{{ item.name }}</span>
                        <span v-if="index === 1" class="grey--text caption"> (+{{ selectedColumns.length - 1 }} others)</span>
                    </template>
                    <template v-slot:append-item>
                        <v-divider class="mb-2"></v-divider>
                        <v-list-item disabled>
                            <v-list-item-avatar color="grey lighten-3">
                            <v-icon color="grey darken">mdi-table-column</v-icon>
                            </v-list-item-avatar>

                            <v-list-item-content v-if="allColumnsSelected">
                                <v-list-item-title>All columns selected!</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-content v-else-if="someColumnsSelected">
                                <v-list-item-title>Selected Column Count</v-list-item-title>
                                <v-list-item-subtitle>{{ selectedColumns.length }}</v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-content v-else>
                                <v-list-item-title>
                                    Select columns above
                                </v-list-item-title>
                                <v-list-item-subtitle>
                                    click on a column to select for charting
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </template>
                </v-select>
            </v-col>


            <v-col cols="12" sm="6">
                <v-text-field light ref="lowThresh" v-model="lowThreshold" label="Low Threshold" color="#c42111" type="number" :disabled="multipleAxis" :rules="[validation.validNumber(lowThreshold), validation.lowHighValid(lowThreshold, highThreshold)]" />
            </v-col>
            <v-col cols="12" sm="6">
                <v-select light v-model="lowThresholdColor" return-object :items="colorOptions" label="Low Threshold Color" color="#c42111" type="number" :disabled="multipleAxis">
                    <template v-slot:selection="{item}">
                        <v-icon class="mr-1 color-selected" :color="item.value">mdi-checkbox-blank-circle</v-icon>
                        <span v-text="item.text" />
                    </template>
                    <template v-slot:item="{item}">
                        <v-icon class="mr-3 color-option" :color="item.value">mdi-checkbox-blank-circle</v-icon>
                        <span v-text="item.text" />
                    </template>
                </v-select>
            </v-col>

            <v-col cols="12" sm="6">
                <v-text-field light ref="highThresh" v-model="highThreshold" label="High Threshold" color="#c42111" type="number" :disabled="multipleAxis" :rules="[validation.validNumber(lowThreshold), validation.lowHighValid(lowThreshold, highThreshold)]" />
            </v-col>
            <v-col cols="12" sm="6">
                <v-select light v-model="highThresholdColor" return-object :items="colorOptions" label="High Threshold Color" color="#c42111" type="number" :disabled="multipleAxis">
                    <template v-slot:selection="{item}">
                        <v-icon class="mr-1 color-selected" :color="item.value">mdi-checkbox-blank-circle</v-icon>
                        <span v-text="item.text" />
                    </template>
                    <template v-slot:item="{item}">
                        <v-icon class="mr-3 color-option" :color="item.value">mdi-checkbox-blank-circle</v-icon>
                        <span v-text="item.text" />
                    </template>
                </v-select>
            </v-col>


            <v-col cols="12">
                <v-switch light v-model="multipleAxis" color="#c42111" label="Multiple Y-Axis" @change="setMultipleAxisDefaultValues" />
            </v-col>

            <div class="multiple-axis-container" style="width: 100%;" v-if="multipleAxis">
                <v-divider class="mb-2" />

                    <template v-for="(axis, index) in axisList">
                        <v-row dense align="center">
                            <v-col cols="auto">
                                <span class="ml-5 mr-3 multi-axis-label">{{ `Y-Axis ${index + 1}` }}</span>
                            </v-col>
                            <v-col cols="4" lg="4">
                                <v-autocomplete light autocomplete="off" multiple label="Axis Metrics" color="#c42111" v-model="axis.metrics" :items="(selectedMetrics.map(m => {return {name: m}}))" item-text="name" item-value="name" :item-disabled="(value) => multipleAxisRowDisabledCheck(index, value)" :rules="[(() => !!axis.metrics.length || 'This field is required'), validation.allMetrics(selectedMetrics.length, totalAxisMetricsLength)]"></v-autocomplete>
                            </v-col>
                            <v-col cols="2" lg="1">
                                <v-select light v-model="axis.type" :items="chartTypes" label="Chart Type" color="#c42111" />
                            </v-col>
                            <v-col cols="2" lg="1">
                                <v-text-field light label="Axis Units" color="#c42111" v-model="axis.units" />
                            </v-col>
                            <v-col cols="3" lg="2">
                                <v-text-field light label="Axis Label" color="#c42111" v-model="axis.label" :rules="[() => !!axis.label.length || 'This field is required']" />
                            </v-col>
                            <v-col cols="auto">
                                <v-switch light class="mt-6" label="Right Side" color="#c42111" v-model="axis.right" />
                            </v-col>
                            <v-col cols="auto">
                                <v-btn large icon @click="axisList.splice(index, 1)" class="mt-0" color="#ff5252"><v-icon large>mdi-delete-forever</v-icon></v-btn>
                            </v-col>
                        </v-row>
                    </template>

                    <v-btn class="ml-5" color="#c42111" @click="axisList.push({metrics: [], type: selectedChartType, units: '', label: '', right: false})"><v-icon class="mr-2">mdi-plus-circle</v-icon>Axis</v-btn>


                <v-divider class="mt-6 mb-8" />
            </div>

        </v-row>
        </v-form>

        <v-btn class="build-button" color="#c42111" @click="buildChart(false)"><v-icon left>mdi-play</v-icon>Build Chart</v-btn>

    </div>
</template>

<script>
export default {
    name: 'ReportWidgetChart',
    props: ['transpose', 'reportDetails', 'reportResults', 'datetimeColumn', 'savedChartOptions', 'refeshChartSettings', 'refreshChartRun'],
    components: {

    },
    data: () => ({
        formValid: true,
        chartType: null,
        chartOptions: null,
        chartTitle: "",
        chartSubtitle: "",
        chartTypes: ['Area', 'Bar', 'Column', 'Line'],
        xAxisTitle: "",
        yAxisTitle: "",
        selectedRows: [],
        rowsList: [],
        selectedLegendValues: [],
        legendValues: [],
        selectedColumns: [],
        columnList: [],
        metricsList: [],
        selectedMetrics: [],
        hideMetricsLegend: false,
        hideLegend: false,
        networkElementsList: [],
        selectedNetworkElements: [],
        networkElementsImmutableStrings: [],
        selectedNetworkElementsImmutableStrings: [],
        colorOptions: [
            {text: 'Red', value: '#FF0000'},
            {text: 'Pink', value: '#FFC0CB'},
            {text: 'Orange', value: '#FFA500'},
            {text: 'Yellow', value: '#FFFF00'},
            {text: 'Violet', value: '#EE82EE'},
            {text: 'Purple', value: '#800080'},
            {text: 'Green', value: '#008000'},
            {text: 'Light Green', value: '#90EE90'},
            {text: 'Teal', value: '#008080'},
            {text: 'Aqua', value: '#00FFFF'},
            {text: 'Blue', value: '#0000FF'},
            {text: 'Brown', value: '#A52A2A'},
            {text: 'Gray', value: '#808080'},
            {text: 'Dark Gray', value: '#2F4F4F'},
        ],
        lowThreshold: null,
        lowThresholdColor: null,
        highThreshold: null,
        highThresholdColor: null,
        multipleAxis: false,
        axisList: [],
        chartColors: ["#f7a35c", "#7cb5ec", "#278713", "#8085e9", "#f15c80", "#e4d354", "#2b908f", "#f45b5b", "#91e8e1"],
        validation: {
            allMetrics: (selectedMetricsLength, totalAxisMetricsLength) => (selectedMetricsLength == totalAxisMetricsLength) || 'Not all metrics accounted for',
            validNumber: (value) => (!isNaN(Number(value)) || `Value must be valid number`),
            lowHighValid: (low, high) => (!low || !high || Number(low) < Number(high) || 'Low Threshold must be below High Threshold'),
        }
    }),

    computed: {
        allNetworkElementsSelected () {
            return this.selectedNetworkElements.length === this.networkElementsList.length;
        },
        someNetworkElementsSelected () {
            return this.selectedNetworkElements.length > 0 && !this.allNetworkElementsSelected;
        },
        networkElementIcon () {
            if (this.allNetworkElementsSelected) return 'mdi-close-box';
            if (this.someNetworkElementsSelected) return 'mdi-minus-box';
            return 'mdi-checkbox-blank-outline';
        },

        allColumnsSelected () {
            return this.selectedColumns.length === this.columnList.length
        },
        someColumnsSelected () {
            return this.selectedColumns.length > 0 && !this.allColumnsSelected
        },
        columnIcon () {
            if (this.allColumnsSelected) return 'mdi-close-box'
            if (this.someColumnsSelected) return 'mdi-minus-box'
            return 'mdi-checkbox-blank-outline'
        },

        totalAxisMetricsLength () {
            let total = 0;
            this.axisList.forEach(a => {
                total += a.metrics.length;
            });
            return total;
        },

        selectedChartType () {
            return this.chartType.toLowerCase();
        },

        hideMetricsLegendDisabled() {
            if(this.hideLegend) {
                if(this.hideMetricsLegend) {
                    this.hideMetricsLegend = false;
                }
                return true;
            }
            return false;
        }
    },

    methods: {
        buildResourceLists: function() {
            if(!this.$props.transpose) {
                this.rowsList = this.$props.reportResults.data;
                this.columnList = this.$props.reportResults.cols.filter(col => {
                    return (col.type.toLowerCase() == "f" || col.type.toLowerCase() == "i");
                });
            }
            else {
                //Transpose Build. Little more complicated
                let columns = [];
                let columnObjs = [];
                let timeColumn = this.$props.datetimeColumn;
                let formattedRowNames = [];

                // LEGEND OPTIONS - build this list.
                this.legendValues = this.$props.reportResults.cols.filter(c => c.graph_row == true && c.name != "").map(c => {return c.name});
                if(!this.selectedLegendValues.length) {
                    // If this is the first time theyre editing the chart then we want to have all values selected by default.
                    this.selectedLegendValues = this.legendValues;
                }

                // COLUMNS - dates
                // This builds a list of all the different datetimes. 1 value for every row of data.
                for(let row of this.$props.reportResults.data) {
                    columns.push(row[timeColumn]);
                }
                // Chrono sort dates.
                columns.sort((a,b) => {
                    return new Date(a) - new Date(b);
                });
                // Remove the duplicates?? Yep. Theres a duplicate time col for each metric. Eg so 4 different metrics means 4x the cols
                columns = [...new Set(columns)];
                columns.forEach(col => {
                    columnObjs.push({name: col});
                });
                this.columnList = columnObjs;


                // METRICS - get a list of columns that are numeric types because those are the ones we can graph
                let numericColumns = this.$props.reportResults.cols.filter(col => {
                    return (col.type.toLowerCase() == "f" || col.type.toLowerCase() == "i");
                });
                this.metricsList = numericColumns.map(c => c.name);


                // NETWORK ELEMENTS - Get list of unique row names
                let rows = this.$props.reportResults.data;
                let listOfRowsImmutableStrings = [];
                let rowImmutableStrings = [];
                for(let row of rows) {
                    let rowNameArr = row.row_name.split(' ').filter(text => {
                        return (!(TELSA.moment(text, 'YYYY-MM-DD', true).isValid() 
                            || TELSA.moment(text, 'YYYY-MM-DD HH:mm:ss', true).isValid() // impossible?
                            || TELSA.moment(text, 'HH:mm:ss', true).isValid()
                            || TELSA.moment(text, 'YYYY-MM-DD HH:mm:ssZ', true).isValid() // impossible?
                            || TELSA.moment(text, 'HH:mm:ssZ', true).isValid()
                            || TELSA.moment(text, 'YYYY-WW', true).isValid()));
                    });
                    row.rowNameFormatted = rowNameArr.join(' ');
                    formattedRowNames.push(row.rowNameFormatted);

                    // Build list of network elements immutable strings
                    rowImmutableStrings = [];
                    for(let x of row.immutable_row_name) {
                        rowImmutableStrings.push(x.value);
                    }
                    listOfRowsImmutableStrings.push(rowImmutableStrings.join(' '));
                }
                this.networkElementsList = [...new Set(formattedRowNames)];
                this.networkElementsImmutableStrings = [...new Set(listOfRowsImmutableStrings)];
            }
        },

        toggleSelectAllNetworkElements () {
            if (this.allNetworkElementsSelected) {
                this.selectedNetworkElements = [];
            } else {
                this.selectedNetworkElements = this.networkElementsList.slice();
            }
        },

        toggleSelectAllColumns () {
            if (this.allColumnsSelected) {
                this.selectedColumns = [];
            } else {
                this.selectedColumns = this.columnList.slice();
            }
        },

        buildChart: function(refresh) {
            if(!this.$refs.chartForm.validate()) {
                return;
            }

            let selectedColumnNames = [];
            let seriesData = [];
            let yAxisList = [];
            let lowThresExceeded = false;
            let highThresExceeded = false;

            if(!this.$props.transpose) {
                // Non-Tanspose pretty simple
                for(let column of this.selectedColumns) {
                    selectedColumnNames.push(column.name);
                }

                for(let selectedRow of this.selectedRows) {
                    let data = [];
                    for(let selectedCol of this.selectedColumns) {
                        //data.push(Number(selectedRow[selectedCol.name]));
                        data.push(Number(String(selectedRow[selectedCol.name]).replace(/,/g, '')));
                    }
                    seriesData.push({
                        name: selectedRow.row_name,
                        data: data
                    });
                }
            }
            else {
                // Transpose data. little more complicated
                for(let column of this.selectedColumns) {
                    selectedColumnNames.push(column.name);
                }

                // Check if their network elements that they had selected have changed since last time.
                // If their saved widgets are new enough to contain the selectedNetworkElementsImmutableStrings then 
                // can use that to automatically select the correct network element. If their widgets are old and dont have 
                // that property, then the best we can do is to deselect the network element and prevent it from erroring. 
                for(let i = this.selectedNetworkElements.length - 1; i >= 0; i--) {
                    let selectedNetworkElement = this.selectedNetworkElements[i];
                    if(!this.networkElementsList.includes(selectedNetworkElement)) {
                        if(this.selectedNetworkElementsImmutableStrings.length) {
                            // We should be able to find the new network element to select.
                            let idx = this.selectedNetworkElements.indexOf(selectedNetworkElement); // get the index of the networkElement that we cant find.
                            let selectedNetworkElementsImmutableString = this.selectedNetworkElementsImmutableStrings[idx]; // get the corresponding immutable string for that networkElement we cant find.
                            idx = this.networkElementsImmutableStrings.indexOf(selectedNetworkElementsImmutableString); // Now that we have the immutable string we can get the new network element.
                            if(idx == -1) {
                                // this probably means that the network element that we cant find was actually deleted(site deleted), and not just renamed. So now we just remove it from the selection.
                                this.selectedNetworkElements.splice(this.selectedNetworkElements.indexOf(selectedNetworkElement), 1);
                            }
                            else {
                                let newNetworkElement = this.networkElementsList[idx]; // since the indexes of the networkElements list and the immutableStrings list are in sync, we can use the index to get the matching value.
                                this.selectedNetworkElements.splice(this.selectedNetworkElements.indexOf(selectedNetworkElement), 1, newNetworkElement); // replace old selected network element with the new one.
                            }
                        }
                        else {
                            // We are not able to select the new network element, so lets just de-select the unknown one.
                            this.selectedNetworkElements.splice(this.selectedNetworkElements.indexOf(selectedNetworkElement), 1);
                        }
                        

                        
                    }
                }

                let reportDataRows = this.reportResults.data;
                let timeColumn = this.$props.datetimeColumn;
                let dataFound = false;
                for(let metric of this.selectedMetrics) {
                    for(let networkElement of this.selectedNetworkElements) {
                        let data = [];
                        let rowZero = null;
                        for(let selectedCol of this.selectedColumns) {
                            dataFound = false;
                            for(let dataRow of reportDataRows) {
                                if(dataRow.rowNameFormatted == networkElement && dataRow[timeColumn] == selectedCol.name) {
                                    rowZero = rowZero || dataRow;
                                    data.push(Number(dataRow[metric].replace(/,/g, '')));
                                    dataFound = true;
                                    break;
                                }
                            }
                            if(!dataFound) {
                                data.push(null);
                            }
                        }

                        let seriesDataLegendName = "";
                        for(let legendCol of this.selectedLegendValues) {
                            seriesDataLegendName += `${rowZero[legendCol]} `;
                        }
                        seriesData.push({
                            //name: `${metric} - ${seriesDataLegendName}`,
                            name: `${this.hideMetricsLegend ? "" : (metric + ' - ')}${seriesDataLegendName}`,
                            metric: metric,
                            networkElement: networkElement,
                            data: data,
                            showInLegend: !this.hideLegend
                        });
                    }
                }

                if(this.multipleAxis) {
                    // create the yAxis's
                    let axisColor = null;
                    for(let [index, axis] of this.axisList.entries()) {
                        axisColor = null;

                        for(let [seriesIndex, series] of seriesData.entries()) {
                            if(axis.metrics.includes(series.metric)) {
                                series.yAxis = index;
                                series.type = axis.type.toLowerCase();
                                axisColor = (axisColor ? '#2F2F2F' : this.chartColors[seriesIndex]);
                            }
                        }

                        yAxisList.push({
                            title: {
                                text: axis.label,
                                style: {
                                    color: axisColor
                                }
                            },
                            labels: {
                                format: `{value} ${axis.units}`,
                                style: {
                                    color: axisColor
                                }
                            },
                            opposite: axis.right
                        });
                    }
                }

                let weeklySummaryReport = (this.$props.reportDetails && this.$props.reportDetails.report_params && this.$props.reportDetails.report_params.some(x => x.humanText && x.humanText.toLowerCase() == "weekly summary"));
                for(let seriesDataItem of seriesData) {
                    // This is formatting the dates to unix times so highcharts can build the custom datetime ranges on the x-axis
                    for (let [i, col] of selectedColumnNames.entries()) {
                        let yVal = seriesDataItem.data[i];
                        let xVal

                        // We have to subract the tzOffset(convert minutes to seconds) because converting a time to unix() always makes it UTC time.
                        //let xVal = ((TELSA.moment(col).unix() - (new Date().getTimezoneOffset() * 60)) * 1000);
                        //let xVal = (TELSA.moment(col).unix() * 1000);
                        if(TELSA.moment(col, 'YYYY-MM-DD HH:mm:ssZ', true).isValid()) {
                            //console.log('LD DOING WEIRD TZ STUFF!!!');
                            xVal = (TELSA.moment(col).unix() * 1000);
                            xVal += ((3600 * Number(col.substr(-3))) * 1000);
                        }
                        else {
                            // It looks like the timezone parsing failed so we need to get it the old-fashioned way. We also need to account for 
                            // "week number" format. moment interprets XXXX-XX as YYYY-MM but it could be YYYY-WW. So check the report params for "Weekly Summary"
                            if(weeklySummaryReport && /^([0-9][0-9][0-9][0-9]-[0-9][0-9])$/.test(col)) {
                                xVal = ((TELSA.moment(col, 'YYYY-WW').unix() - (new Date().getTimezoneOffset() * 60)) * 1000);
                            }
                            else {
                                xVal = ((TELSA.moment(col).unix() - (new Date().getTimezoneOffset() * 60)) * 1000);
                            }
                        }
                        seriesDataItem.data[i] = [xVal, yVal];
                    }
                    if(!this.multipleAxis) {
                        let lastVal = seriesDataItem.data[seriesDataItem.data.length - 1];
                        this.lowThresExceeded = (this.lowThresExceeded || (!!this.lowThreshold && lastVal[1] < Number(this.lowThreshold)));
                        this.highThresExceeded = (this.highThresExceeded || (!!this.highThreshold && lastVal[1] > Number(this.highThreshold)));
                    }
                }
            }

            // build the list of selectedNetworkElementsImmutableStrings
            this.selectedNetworkElementsImmutableStrings = [];
            for(let selectedNetworkElement of this.selectedNetworkElements) {
                let idx = this.networkElementsList.indexOf(selectedNetworkElement);
                this.selectedNetworkElementsImmutableStrings.push(this.networkElementsImmutableStrings[idx]);
            }

            this.chartOptions = {
                colors: this.chartColors,
                title: {
                    text: this.chartTitle,
                    style: {
                        color: "#2F2F2F",
                        fontWeight: "bold"
                    }
                },
                subtitle: {
                    text: this.chartSubtitle,
                    useHTML: true,
                    widthAdjust: -200,
                    style: {
                        color: "#2F2F2F"
                    }
                },
                chart: {
                    type: this.selectedChartType,
                    backgroundColor: "#FFFFFF",
                    color: "#2F2F2F",
                    width: this.$el.parentElement.parentElement.parentElement.offsetWidth,
                    height: this.$el.parentElement.parentElement.parentElement.offsetHeight,
                    borderRadius: "5px",
                    inverted: false,
                    zoomType: 'x',
                    styledMode: false
                },
                credits: {
                    enabled: false
                },
                legend: {
                    itemStyle: {
                        color: "#2F2F2F",
                    },
                    enabled: true
                },
                xAxis: {
                    // Columns
                    categories: (this.$props.transpose ? null : selectedColumnNames),
                    title: {
                        text: this.xAxisTitle
                    },
                    labels: {
                        enabled: true,
                        // style: {
                        //     color: "#2F2F2F"
                        // }
                    },
                    type: 'datetime'
                },
                yAxis: this.multipleAxis ? yAxisList : {
                    title: {
                        text: this.yAxisTitle
                    },
                    labels: {
                        style: {
                            color: "#2F2F2F",
                        }
                    },
                    plotLines: [
                        {
                            color: this.lowThresholdColor ? this.lowThresholdColor.value : '#FF0000',
                            width: 4,
                            value: this.lowThreshold ? this.lowThreshold : null,
                        },
                        {
                            color: this.highThresholdColor ? this.highThresholdColor.value : '#FF0000',
                            width: 4,
                            value: this.highThreshold ? this.highThreshold : null,
                        }
                    ]
                },
                series: seriesData,
                plotOptions: {
                    series: {
                        point: {
                            events: {
                                mouseOver: function(event) {
                                    if(!self.multipleAxis) {
                                        return;
                                    }
                                    let point = event.target;
                                    let series = point.series;
                                    let highlightColor = '#c42111';

                                    if (point.graphic) {
                                        point.graphic.attr({
                                            fill: highlightColor
                                        });
                                    }

                                    series.chart.series.forEach(function(s) {
                                        if (s.isHighlighted) {
                                            self.highlightSeries(s, s.color);
                                            s.isHighlighted = false;
                                        }
                                    });

                                    self.highlightSeries(series, highlightColor);
                                    series.isHighlighted = true;
                                },
                                mouseOut: function(event) {
                                    if(!self.multipleAxis) {
                                        return;
                                    }
                                    let point = event.target;
                                    let series = point.series;
                                    self.highlightSeries(series, series.color);
                                }
                            }
                        }
                    }
                },


                selectedRows: this.selectedRows,
                selectedNetworkElements: this.selectedNetworkElements,
                selectedNetworkElementsImmutableStrings: this.selectedNetworkElementsImmutableStrings,
                selectedMetrics: this.selectedMetrics,
                selectedLegendValues: this.selectedLegendValues,
                multipleAxis: this.multipleAxis,
                axisList: this.axisList,
                lowThresExceeded: lowThresExceeded,
                highThresExceeded: highThresExceeded,
                hideLegend: this.hideLegend,
                hideMetricsLegend: this.hideMetricsLegend
            };
            this.$emit('build', this.chartOptions, refresh);
        },


        highlightSeries: function(series, color) {
            for (let key in series.yAxis.ticks) {
                series.yAxis.ticks[key].label.css({
                    fill: color,
                    fontWeight: (color == '#c42111' ? 'bold' : 'inherit'),
                    fontSize: (color == '#c42111' ? '12px' : '11px'),
                });
            }

            series.yAxis.axisTitle.css({
                fill: color,
                fontWeight: (color == '#c42111' ? 'bold' : 'inherit'),
                fontSize: (color == '#c42111' ? '12px' : '11px'),
            });
        },

        setMultipleAxisDefaultValues: function(value) {
            return;
            if(!value) {
                return;
            }
            this.axisList = [];
            for(let row of this.selectedRows) {
                this.axisList.push({
                    rows: [row.row_name],
                    type: this.selectedChartType,
                    units: '',
                    label: row.row_name,
                    right: false
                });
            }
        },

        multipleAxisRowDisabledCheck: function(axisIndex, networkElement) {
            for(let [index, axis] of this.axisList.entries()) {
                if(axisIndex != index && axis.metrics.includes(networkElement.name)) {
                    return true;
                }
            }
            return false;
        }

        

    },

    mounted() {
        if(this.$props.savedChartOptions.chart) {
            let chartOptions = this.$props.savedChartOptions;
            this.chartType = this.chartTypes[(this.chartTypes.map(t => {return t.toLowerCase();})).indexOf(chartOptions.chart.type)];
            this.chartTitle = chartOptions.title.text;
            this.chartSubtitle = chartOptions.subtitle.text;
            this.hideLegend = chartOptions.hideLegend || false;
            this.hideMetricsLegend = chartOptions.hideMetricsLegend || false;

            //this.selectedRows = chartOptions.selectedRows || [];
            // THIS IS TO MIGRATE OLD DASHBOARD CHART DATA TO NEW IMPROVED FORMAT
            if(chartOptions.selectedNetworkElements && chartOptions.selectedMetrics) {
                // New Format
                this.selectedNetworkElements = chartOptions.selectedNetworkElements;
                this.selectedMetrics = chartOptions.selectedMetrics;
                this.selectedLegendValues = chartOptions.selectedLegendValues || [];
            }
            else {
                // Old Format
                let selectedNetworkElements = [];
                let selectedMetrics = [];
                for(let selectedRow of chartOptions.selectedRows) {
                    selectedMetrics.push(selectedRow.row_name.split(' - ')[0]);
                    selectedNetworkElements.push(selectedRow.row_name.split(' - ')[1]);
                }
                this.selectedNetworkElements = [...new Set(selectedNetworkElements)];
                this.selectedMetrics = [...new Set(selectedMetrics)];
            }


            // multiple y-axis
            this.multipleAxis = chartOptions.multipleAxis;
            if(this.multipleAxis) {
                this.axisList = chartOptions.axisList;
            }
            else {
                this.yAxisTitle = chartOptions.yAxis.title.text;
                this.lowThreshold = chartOptions.yAxis.plotLines[0].value;
                this.lowThresholdColor = this.colorOptions.find(c => {return c.value == chartOptions.yAxis.plotLines[0].color});
                this.highThreshold = chartOptions.yAxis.plotLines[1].value;
                this.highThresholdColor = this.colorOptions.find(c => {return c.value == chartOptions.yAxis.plotLines[1].color});
            }

            this.xAxisTitle = chartOptions.xAxis.title.text;

            // Fix for when the selected network elements names have changed
            this.selectedNetworkElementsImmutableStrings = (chartOptions.selectedNetworkElementsImmutableStrings && chartOptions.selectedNetworkElementsImmutableStrings.length) ? chartOptions.selectedNetworkElementsImmutableStrings : [];
        }
        else {
            this.chartTitle = this.$props.reportDetails.report_name;
            this.chartSubtitle = this.$props.reportDetails.human_params;
        }
        this.buildResourceLists();
    },

    watch: {
        refeshChartSettings: function(newVal, oldVal) {
            if(newVal != oldVal) {
                this.buildResourceLists();
            }
        },
        refreshChartRun: function(newVal, oldVal) {
            if(newVal != oldVal) {
                this.buildResourceLists();
                if(!this.allColumnsSelected) {
                    this.toggleSelectAllColumns();
                }
                this.buildChart(true);
            }
        },
    }

  
};
</script>

<style scoped>

    .multi-axis-label {
        display: inline-block;
        font-weight: bold;
        font-size: 18px;
    }

    .color-option {
        text-shadow: -1px 0 #fff, 0 1px #fff, 1px 0 #fff, 0 -1px #fff;
    }

    .color-selected {
        text-shadow: -1px 0 #000, 0 1px #000, 1px 0 #000, 0 -1px #000;
    }

</style>
